import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useAssessDiabetes } from '@hooks/register/screening/use-assess-diabetes';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { assessDiabetesSchema } from '@views/register/screening/assess-diabetes.schema';
import { asString, asStringBool } from '@/utils/parse';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import type { IAssessDiabetesFormValues, IAssessDiabetesModel, IAssessDiabetesSubmitResult } from '@/types/screening/assess-diabetes';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';
import FieldError from '@components/field-error';

// Nkr.Model.Enums.MedicalAssessments.DiabetesStatus (value → label key / default).
const DIABETES_STATUS_OPTIONS: { value: number; labelKey: string; label: string }[] = [
    { value: 1, labelKey: 'DashLabel_ItSMaintainedByDietAndExercise', label: 'Diet and exercise' },
    {
        value: 2,
        labelKey: 'DashLabel_ItSMaintainedWithMedicationEGMetformin',
        label: 'Medication (such as metformin)',
    },
    { value: 3, labelKey: 'DashLabel_ItSMaintainedWithInsulin', label: 'Insulin' },
];

const AssessDiabetes = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useAssessDiabetes(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <AssessDiabetesContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IAssessDiabetesContentProps {
    model: IAssessDiabetesModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IAssessDiabetesFormValues) => Promise<IAssessDiabetesSubmitResult>;
}

const AssessDiabetesContent = ({ model, isSubmitting, submitError, onSubmit }: IAssessDiabetesContentProps) => {
    const t = useLocalize(model.languageData);

    const form = useStepForm<IAssessDiabetesFormValues, IAssessDiabetesSubmitResult>({
        resolver: zodResolver(assessDiabetesSchema),
        defaultValues: {
            hasDiabetes: asStringBool(model.hasDiabetes),
            diabetesStatus: asString(model.diabetesStatus),
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        register,
        watch,
        setValue,
        formState: { errors },
    } = form;

    const showStatus = watch('hasDiabetes') === '1';

    useEffect(() => {
        if (!showStatus) {
            setValue('diabetesStatus', '');
        }
    }, [showStatus, setValue]);

    return (
        <StepFormLayout
            kind="screening"
            model={model}
            step={DashRegistrationStep.AssessDiabetes}
            title={t('DashLabel_ProgressDiabetes', 'Diabetes')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t('DashLabel_DoYouHaveDiabetes', 'Do you have diabetes')}?
                    <FieldError message={errors.hasDiabetes?.message} />
                </label>
                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                    <label className="radio-inline">
                        <input type="radio" value="1" {...register('hasDiabetes')} />{' '}
                        {t('Word_Yes', 'Yes')}
                    </label>
                    <label className="radio-inline">
                        <input type="radio" value="0" {...register('hasDiabetes')} />{' '}
                        {t('Word_No', 'No')}
                    </label>
                </div>
            </div>

            {showStatus && (
                <div className="sub-question" id="status">
                    <label className="control-label col-xs-12">
                        {t(
                            'DashLabel_WhatBestDescribesYourMethodOfBloodSugarControl',
                            'How do you control your blood sugar',
                        )}
                        ?<FieldError message={errors.diabetesStatus?.message} />
                    </label>
                    <div className="col-xs-12" style={{ marginLeft: 10 }}>
                        {DIABETES_STATUS_OPTIONS.map((option) => (
                            <div className="radio" key={option.value}>
                                <label>
                                    <input
                                        type="radio"
                                        value={option.value}
                                        {...register('diabetesStatus')}
                                    />{' '}
                                    {t(option.labelKey, option.label)}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </StepFormLayout>
    );
};

export default AssessDiabetes;
